import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

const reactPlugin = new ReactPlugin();

const connectionString = process.env.ConnectionString;

const appInsights = new ApplicationInsights({
  config: {
    connectionString,
    extensions: [reactPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: { history: null }
    },
    // Auto-capture uncaught errors + unhandled promise rejections so bootstrap/auth
    // failures surface as AI exceptions (previously 0 exceptions reached the portal).
    disableExceptionTracking: false,
    enableUnhandledPromiseRejectionTracking: true,
    // Root cause (2026-07-20, ws-z4wov): the taskpane runs almost exclusively inside an
    // Office dialog (Office.context.ui.displayDialogAsync) that the parent ribbon-command
    // context tears down via dialog.close() ~1s after a successful action (1000ms poll in
    // src/commands/commands.js), well under this SDK's 15s default maxBatchInterval -- so
    // trackEvent/trackTrace/trackException calls were queued and lost before the batch
    // timer ever fired. Lowering the interval makes the batch send fire fast enough to
    // beat that teardown in the common case.
    maxBatchInterval: 500,
  }
});

// PII gate: email-bearing / verbose traces ship only in non-prod (DIRECT_AUTH_ALLOWED).
// Generic event names + counts are safe in all envs. Reuse this everywhere instead of
// re-checking the env flag at each call site.
const isVerboseTelemetry = !!process.env.DIRECT_AUTH_ALLOWED;

// Thin, never-throwing wrappers -- telemetry must not crash the bootstrap flow, and must
// no-op cleanly when App Insights isn't initialized (local dev, empty connection string).
const track = (name, properties) => {
  try {
    appInsights.trackTrace({ message: name, properties: properties || {} });
  } catch (_e) { /* swallow -- telemetry is best-effort */ }
};
const trackEvent = (name, properties) => {
  try {
    appInsights.trackEvent({ name, properties: properties || {} });
  } catch (_e) { /* swallow */ }
};
const trackError = (error, properties) => {
  try {
    appInsights.trackException({ exception: error instanceof Error ? error : new Error(String(error)), properties: properties || {} });
  } catch (_e) { /* swallow */ }
};

// Only initialize when a connection string is configured. Local dev leaves it
// empty (frontend/config/public.js `local` block) -> skip init so App Insights
// doesn't throw "Please provide instrumentation key".
if (connectionString) {
  appInsights.loadAppInsights();

  // Stamp application_Version + cloud role on every outgoing item so App Insights can
  // filter local (AppVersion="local") from deployed dev/test/stage/prod traffic, and
  // one add-in's events from another's -- all three share this one connectionString.
  // Tag keys per the JS SDK's IContextTagKeys (applicationinsights-common typings):
  // applicationVersion -> "ai.application.ver", cloudRole -> "ai.cloud.role".
  try {
    appInsights.addTelemetryInitializer((item) => {
      item.tags = item.tags || {};
      item.tags['ai.application.ver'] = process.env.AppVersion;
      item.tags['ai.cloud.role'] = process.env.CloudRoleName;
    });
  } catch (_e) { /* swallow -- telemetry must not crash the bootstrap flow */ }

  // Defense in depth: the Office dialog host can tear down the webview without a
  // guaranteed browser unload lifecycle, so don't rely solely on the SDK's default
  // onBeforeUnload flush (which may never get the chance to fire here). Force an
  // immediate async flush (sendBeacon when available) on every signal that could
  // precede that teardown.
  const flushTelemetry = () => appInsights.flush();
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flushTelemetry);
    window.addEventListener('beforeunload', flushTelemetry);
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushTelemetry();
    });
  }
}

export { reactPlugin, appInsights, track, trackEvent, trackError, isVerboseTelemetry };